<template>
  <section :class="$style.wrapper">
    <header :class="$style.header">
      <h2 :class="$style.title">担当者</h2>
      <ButtonClose @click="emit('close')" />
    </header>
    <section :class="$style.body">
      <DolphinTable
        v-if="rows.length > 0"
        :class="$style.table"
        :columns="columns"
        :rows="rows"
      >
        <template #delete="payload">
          <FlatButton
            :disabled="!canDeleteAccount(payload)"
            :class="$style.addButton"
            size="small"
            variant="caution"
            @click="deleteRole(payload)"
          >
            削除
          </FlatButton>
        </template>
      </DolphinTable>
      <p :class="$style.empty" v-else>登録されていません</p>
      <div v-if="isTeamAdministratorMember" :class="$style.action">
        <p>新規追加</p>
        <DolphinSelector
          :class="$style.selector__action"
          placeholder="---------------"
          :options="staffOptions"
          v-model="selectedId"
          small
        />
        <FlatButton
          :class="$style.addButton"
          size="small"
          variant="primary"
          @click="add"
        >
          ＋追加
        </FlatButton>
      </div>
    </section>
  </section>
</template>

<script lang="ts" setup>
import { defineResponseHandlers } from '@/libs/utilities/graphql'
import type { Column } from '@/components/organisms/DolphinTable/DolphinTable.vue'
import DolphinTable from '@/components/organisms/DolphinTable/DolphinTable.vue'
import { computed, ref } from 'vue'
import ButtonClose from '@/components/atoms/ButtonClose.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import {
  useMatchAssignedStaffManagerModalQuery,
  type MatchAssignedStaffManagerModalStaffFragment,
  useAssignStaffToMatchMutation,
  useUnassignStaffFromMatchMutation,
  type UnassignStaffFromMatchMutation,
} from './MatchAssignedPlannerAccountManagerModal.generated'
import { useAlert } from '@/libs/alertPortal'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import type { Option } from '@/types/app'

const props = defineProps<{
  chatRoomId: number
  isTeamAdministratorMember: boolean
}>()

const emit = defineEmits<{
  close: []
  updatedRole: []
}>()

const { result } = useMatchAssignedStaffManagerModalQuery({
  chatRoomId: props.chatRoomId.toString(),
})

const { mutate: assign } = useAssignStaffToMatchMutation({})

const { mutate: unassign } = useUnassignStaffFromMatchMutation({})

const alert = useAlert()

const staffs = computed((): MatchAssignedStaffManagerModalStaffFragment[] => {
  if (!result.value) {
    return []
  }

  return result.value.staffsByPlanner
})

const columns = computed((): Column<unknown>[] => [
  {
    label: '担当',
    key: 'assignedStaff',
  },
  {
    label: '権限',
    key: 'role',
  },
  {
    label: '削除',
    key: 'delete',
  },
])

type Row = {
  assignedStaff: string
  role: string
  delete: string
}

const rows = computed((): Row[] => {
  if (!result.value?.ownChatRoom) {
    return []
  }

  return result.value.ownChatRoom.match.assignedStaffs.map(
    ({ guid, displayName }) => ({
      assignedStaff: displayName,
      role: 'チャット送信・閲覧', // 今は一個だけ
      delete: guid,
    })
  )
})

const selectedId = ref<string | null>(null)

const staffOptions = computed<Option<string | null>[]>(() => [
  {
    value: null,
    text: '-----',
  },
  ...staffs.value
    .filter(({ guid }) => {
      return !result.value?.ownChatRoom?.match.assignedStaffs.some(
        staff => staff.guid === guid
      )
    })
    .map(({ guid, displayName }) => ({
      value: guid,
      text: displayName,
    })),
])
const add = async () => {
  if (!result.value?.ownChatRoom || !selectedId.value) {
    return []
  }

  await assign({
    input: {
      staffGUID: selectedId.value,
      matchID: result.value.ownChatRoom.match.id,
    },
  })

  emit('updatedRole')

  selectedId.value = null
}

const deleteRole = async ({ value: id }: { value: string }) => {
  if (!result.value?.ownChatRoom) {
    return []
  }

  if (!window.confirm('権限を削除しますか？')) {
    return
  }

  const data = await unassign({
    input: {
      staffGUID: id,
      matchID: result.value.ownChatRoom.match.id,
    },
  })

  if (data?.errors || !data?.data) {
    throw new Error('mutation failed')
  }

  defineResponseHandlers<
    UnassignStaffFromMatchMutation['unassignStaffFromMatch'],
    void
  >({
    UnassignStaffFromMatchErrorNotAuthorityPayload: () => {
      alert.error('権限がありません')
    },
    UnassignStaffFromMatchSuccessPayload: () => {
      alert.success('権限を削除しました')
      emit('updatedRole')
    },
  })(data.data.unassignStaffFromMatch)
}

const canDeleteAccount = ({ value: id }: { value: string }): boolean => {
  if (props.isTeamAdministratorMember) {
    return true
  }

  if (id === result.value?.viewer.staff.guid) {
    return true
  }

  return false
}
</script>

<style lang="scss" module>
.wrapper {
  width: 90vw;
  max-width: 620px;
}

.header {
  display: flex;
  width: 100%;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid $color-border-low-emphasis;
}

.title {
  font-size: 16px;
  font-weight: bold;
}

.body {
  padding: 10px 40px 40px;

  &__buttonWrapper {
    margin-top: 44px;
  }
}

.table {
  width: 100%;
}

.action {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;

  &__button {
    display: block;
  }
}

.empty {
  margin-top: 10px;
}
</style>
